import React, { createContext, useContext, useState, useEffect } from 'react';

const AssetFormContext = createContext();

export const useAssetForm = () => {
    const context = useContext(AssetFormContext);
    if (!context) {
        throw new Error('useAssetForm must be used within AssetFormProvider');
    }
    return context;
};

export const AssetFormProvider = ({ children }) => {
    const [addFormData, setAddFormData] = useState({
        description: '',
        assetTag: '',
        purchaseDate: '',
        purchasedFrom: '',
        brand: '',
        model: '',
        serialNumber: '',
        cost: '',
        site: '',
        location: '',
        category: '',
        department: '',
    });

    const [addPhotoPreview, setAddPhotoPreview] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [editPhotoPreview, setEditPhotoPreview] = useState({});

    // No sessionStorage needed! Context API state persists during navigation
    // and automatically clears on page refresh (which is what we want)

    const updateAddFormData = (updates) => {
        setAddFormData(prev => ({ ...prev, ...updates }));
    };

    const setAddFormField = (name, value) => {
        setAddFormData(prev => ({ ...prev, [name]: value }));
    };

    const clearAddFormData = () => {
        setAddFormData({
            description: '',
            assetTag: '',
            purchaseDate: '',
            purchasedFrom: '',
            brand: '',
            model: '',
            serialNumber: '',
            cost: '',
            site: '',
            location: '',
            category: '',
            department: '',
        });
        setAddPhotoPreview(null);
    };

    const setEditFormDataForAsset = (assetId, data) => {
        setEditFormData(prev => ({ ...prev, [assetId]: data }));
    };

    const getEditFormDataForAsset = (assetId) => {
        return editFormData[assetId] || null;
    };

    const setEditPhotoPreviewForAsset = (assetId, preview) => {
        setEditPhotoPreview(prev => ({ ...prev, [assetId]: preview }));
    };

    const getEditPhotoPreviewForAsset = (assetId) => {
        return editPhotoPreview[assetId] || null;
    };

    const updateEditFormDataForAsset = (assetId, updates) => {
        setEditFormData(prev => {
            const current = prev[assetId] || {};
            const updated = { ...current, ...updates };
            return { ...prev, [assetId]: updated };
        });
    };

    const setEditFormFieldForAsset = (assetId, name, value) => {
        setEditFormData(prev => {
            const current = prev[assetId] || {};
            const updated = { ...current, [name]: value };
            return { ...prev, [assetId]: updated };
        });
    };

    const clearEditFormDataForAsset = (assetId) => {
        setEditFormData(prev => {
            const updated = { ...prev };
            delete updated[assetId];
            return updated;
        });
        setEditPhotoPreview(prev => {
            const updated = { ...prev };
            delete updated[assetId];
            return updated;
        });
    };

    const value = {
        // Add form
        addFormData,
        addPhotoPreview,
        updateAddFormData,
        setAddFormField,
        setAddPhotoPreview,
        clearAddFormData,

        // Edit form
        setEditFormDataForAsset,
        getEditFormDataForAsset,
        updateEditFormDataForAsset,
        setEditFormFieldForAsset,
        setEditPhotoPreviewForAsset,
        getEditPhotoPreviewForAsset,
        clearEditFormDataForAsset,
    };

    return (
        <AssetFormContext.Provider value={value}>
            {children}
        </AssetFormContext.Provider>
    );
};

